import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Zap, Plus, Pencil, Trash2, AlertTriangle, Power, PowerOff, Loader2, Tag, X, Copy
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CICLOS = [
  { value: 'simples', label: 'Simples', tipo: 'BTN' },
  { value: 'bi_horario', label: 'Bi-horário', tipo: 'BTN' },
  { value: 'tri_horario', label: 'Tri-horário', tipo: 'BTE' },
];

const SEGMENTS = [
  { value: 'residencial', label: 'Residencial' },
  { value: 'empresarial', label: 'Empresarial' },
];

// Potências por ciclo (mono-ciclo: cada tarifário tem exactamente 1 ciclo).
// Simples (BTN): 1.15 → 20.70
// Bi-horário (BTN): 3.45 → 20.70 (exclui 1.15 e 2.3)
// Tri-horário (BTE): 27.60, 34.50, 41.40
const POTENCIAS_BTN_SIMPLES = [1.15, 2.3, 3.45, 4.6, 5.75, 6.9, 10.35, 13.8, 17.25, 20.7];
const POTENCIAS_BTN_BI = POTENCIAS_BTN_SIMPLES.filter((p) => p >= 3.45);
const POTENCIAS_BTE_TRI = [27.6, 34.5, 41.4];
const POTENCIAS_BY_CICLO = {
  simples: POTENCIAS_BTN_SIMPLES,
  bi_horario: POTENCIAS_BTN_BI,
  tri_horario: POTENCIAS_BTE_TRI,
};

// Helper: returns the single ciclo of a tariff (defaults to 'simples' for legacy/missing).
const getCicloUnico = (form) => (form?.ciclos && form.ciclos[0]) || 'simples';

// Helper: returns potencias_pricing for a given ciclo, preserving existing values.
const buildPotenciasPricingForCiclo = (ciclo, existingPricing = []) => {
  const valid = POTENCIAS_BY_CICLO[ciclo] || POTENCIAS_BTN_SIMPLES;
  const existingMap = {};
  (existingPricing || []).forEach((p) => { existingMap[Number(p.potencia)] = p.termo_fixo_eur_dia; });
  return valid.map((p) => ({ potencia: p, termo_fixo_eur_dia: existingMap[p] ?? 0 }));
};

const DISCOUNT_TYPES = [
  { value: 'flexivel', label: '🎯 Desconto Flexível (recomendado)' },
  { value: 'percent_energy', label: '% sobre Energia' },
  { value: 'percent_total', label: '% sobre Total' },
  { value: 'fixed_monthly', label: '€ Fixo Mensal' },
  { value: 'debito_directo', label: 'Débito Directo (%)' },
  { value: 'factura_electronica', label: 'Factura Electrónica (%)' },
  { value: 'dual', label: 'Dual (%)' },
  { value: 'permanencia', label: 'Permanência (%)' },
  { value: 'consumo_escalonado', label: 'Consumo Escalonado' },
  { value: 'campanha_temporaria', label: 'Campanha Temporária' },
  { value: 'bonus_temporario', label: 'Bónus Temporário (€/mês x N meses)' },
];

const FLEX_BASES = [
  { value: 'termo_energia', label: 'Termo fixo + Energia' },
  { value: 'termo_fixo', label: 'Termo fixo' },
  { value: 'energia', label: 'Energia (kWh)' },
  { value: 'total', label: 'Total' },
];

const DEFAULT_POTENCIAS_PRICING = POTENCIAS_BTN_SIMPLES.map((p) => ({ potencia: p, termo_fixo_eur_dia: 0 }));
const DEFAULT_ESCALOES_PRICING = [1, 2, 3, 4].map((e) => ({ escalao: e, termo_fixo_eur_dia: 0, preco_kwh: 0 }));

const emptyTariff = (commercializer_id, fuel = 'luz') => ({
  commercializer_id: commercializer_id || '',
  name: '',
  fuel,
  segment: 'residencial',
  ciclos: ['simples'],
  potencias: [...POTENCIAS_BTN_SIMPLES],
  potencias_pricing: DEFAULT_POTENCIAS_PRICING.map((x) => ({ ...x })),
  escaloes: DEFAULT_ESCALOES_PRICING.map((x) => ({ ...x })),
  permanencia_meses: 0,
  termo_fixo_eur_dia: 0,
  preco_simples_kwh: 0,
  preco_ponta_kwh: 0,
  preco_cheia_kwh: 0,
  preco_vazio_kwh: 0,
  iva_percent: 23,
  active: true,
  notes: '',
  valid_from: '',
  valid_to: '',
  discounts: [],
});

const CommercializerDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const [form, setForm] = useState({ name: '', color: '#2D3E6F', active: true });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setForm(initial || { name: '', color: '#2D3E6F', active: true });
    }
  }, [open, initial]);

  const save = async () => {
    if (!form.name.trim()) return toast.error('Nome obrigatório');
    setSaving(true);
    try {
      if (initial?.id) {
        await axios.put(`${API_URL}/simulador/commercializers/${initial.id}`, form);
        toast.success('Comercializadora actualizada');
      } else {
        await axios.post(`${API_URL}/simulador/commercializers`, form);
        toast.success('Comercializadora criada');
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro a gravar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="commercializer-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.id ? 'Editar Comercializadora' : 'Nova Comercializadora'}</DialogTitle>
          <DialogDescription>Defina nome, cor identificativa e estado da comercializadora.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Nome</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="commercializer-name-input" />
          </div>
          <div className="flex items-center gap-3">
            <Label className="flex-1">Cor</Label>
            <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-16 h-10 rounded cursor-pointer" />
            <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-32" />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })} id="com-active" />
            <Label htmlFor="com-active">Activa</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="commercializer-save-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TariffDialog = ({ open, onOpenChange, initial, commercializers, defaultCommercializerId, defaultFuel = 'luz', onSaved }) => {
  const [form, setForm] = useState(emptyTariff(defaultCommercializerId, defaultFuel));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (initial?.id) {
        // Editing: normalize multi-ciclo legacy tariffs to mono-ciclo (keep first)
        const ciclo = (initial.ciclos && initial.ciclos[0]) || 'simples';
        const normalized = {
          ...emptyTariff(defaultCommercializerId, initial.fuel || 'luz'),
          ...initial,
          ciclos: [ciclo],
          potencias_pricing: buildPotenciasPricingForCiclo(ciclo, initial.potencias_pricing),
        };
        setForm(normalized);
      } else if (initial) {
        // Duplicating: clone sem id, mas com todos os outros campos
        const ciclo = (initial.ciclos && initial.ciclos[0]) || 'simples';
        const normalized = {
          ...emptyTariff(defaultCommercializerId, initial.fuel || 'luz'),
          ...initial,
          ciclos: [ciclo],
          potencias_pricing: buildPotenciasPricingForCiclo(ciclo, initial.potencias_pricing),
        };
        setForm(normalized);
      } else {
        setForm(emptyTariff(defaultCommercializerId, defaultFuel));
      }
    }
  }, [open, initial, defaultCommercializerId, defaultFuel]);

  // Set single ciclo (mono-ciclo): regenerates potencias_pricing for the new ciclo,
  // preserving prices for potências that exist in both old and new ranges.
  const setCiclo = (c) => {
    setForm((prev) => ({
      ...prev,
      ciclos: [c],
      potencias_pricing: buildPotenciasPricingForCiclo(c, prev.potencias_pricing),
    }));
  };

  const togglePotencia = (p) => {
    const has = form.potencias.includes(p);
    setForm({ ...form, potencias: has ? form.potencias.filter((x) => x !== p) : [...form.potencias, p].sort((a, b) => a - b) });
  };

  const addDiscount = () => {
    setForm({
      ...form,
      discounts: [...(form.discounts || []), { name: '', type: 'percent_energy', value: 0, active: true }],
    });
  };

  const updateDiscount = (idx, patch) => {
    const list = [...(form.discounts || [])];
    list[idx] = { ...list[idx], ...patch };
    setForm({ ...form, discounts: list });
  };

  const removeDiscount = (idx) => {
    const list = [...(form.discounts || [])];
    list.splice(idx, 1);
    setForm({ ...form, discounts: list });
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Nome obrigatório');
    if (!form.commercializer_id) return toast.error('Comercializadora obrigatória');
    const isGas = form.fuel === 'gas';
    const isDual = form.fuel === 'dual';
    const hasLuz = !isGas; // luz ou dual
    if (hasLuz && (!form.ciclos || form.ciclos.length !== 1)) return toast.error('Selecciona exactamente 1 ciclo na secção Luz');
    if (hasLuz && !form.potencias_pricing?.length) return toast.error('Pelo menos 1 potência na secção Luz');
    if (isDual && !(form.escaloes || []).some((e) => Number(e.preco_kwh) > 0 || Number(e.termo_fixo_eur_dia) > 0)) {
      return toast.error('Tarifário Dual: preenche pelo menos 1 escalão de Gás');
    }
    setSaving(true);
    try {
      // Mono-ciclo: align `potencias` array with the potencias_pricing list (single ciclo's valid potências)
      const validPotencias = (form.potencias_pricing || []).map((p) => Number(p.potencia));
      const payload = {
        ...form,
        potencias: validPotencias,
        permanencia_meses: Number(form.permanencia_meses) || 0,
        termo_fixo_eur_dia: Number(form.termo_fixo_eur_dia) || 0,
        preco_simples_kwh: Number(form.preco_simples_kwh) || 0,
        preco_ponta_kwh: Number(form.preco_ponta_kwh) || 0,
        preco_cheia_kwh: Number(form.preco_cheia_kwh) || 0,
        preco_vazio_kwh: Number(form.preco_vazio_kwh) || 0,
        iva_percent: Number(form.iva_percent) || 23,
        potencias_pricing: (form.potencias_pricing || []).map((p) => ({
          potencia: Number(p.potencia) || 0,
          termo_fixo_eur_dia: Number(p.termo_fixo_eur_dia) || 0,
        })),
        escaloes: (form.escaloes || []).map((e) => ({
          escalao: Number(e.escalao) || 0,
          termo_fixo_eur_dia: Number(e.termo_fixo_eur_dia) || 0,
          preco_kwh: Number(e.preco_kwh) || 0,
        })),
        discounts: (form.discounts || []).map((d) => ({
          ...d,
          value: Number(d.value) || 0,
          monthly_eur: d.monthly_eur != null && d.monthly_eur !== '' ? Number(d.monthly_eur) : undefined,
          monthly_eur_gas: d.monthly_eur_gas != null && d.monthly_eur_gas !== '' ? Number(d.monthly_eur_gas) : undefined,
          monthly_eur_dual: d.monthly_eur_dual != null && d.monthly_eur_dual !== '' ? Number(d.monthly_eur_dual) : undefined,
          meses: d.meses != null && d.meses !== '' ? Number(d.meses) : undefined,
          // Campos do tipo flexível (refactor 2026-05-16)
          duracao_meses: d.duracao_meses != null && d.duracao_meses !== '' ? Number(d.duracao_meses) : undefined,
          aplicar_a: d.aplicar_a || undefined,
          base: d.base || undefined,
          value_type: d.value_type || undefined,
          condicoes_required: d.condicoes_required || undefined,
        })),
      };
      if (initial?.id) {
        await axios.put(`${API_URL}/simulador/tariffs/${initial.id}`, payload);
        toast.success('Tarifário actualizado');
      } else {
        await axios.post(`${API_URL}/simulador/tariffs`, payload);
        toast.success('Tarifário criado');
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro a gravar');
    } finally {
      setSaving(false);
    }
  };

  const updatePotenciaPrice = (idx, val) => {
    const list = [...(form.potencias_pricing || [])];
    list[idx] = { ...list[idx], termo_fixo_eur_dia: val };
    setForm({ ...form, potencias_pricing: list });
  };

  const updateEscalao = (idx, patch) => {
    const list = [...(form.escaloes || [])];
    list[idx] = { ...list[idx], ...patch };
    setForm({ ...form, escaloes: list });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="tariff-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.id ? 'Editar Tarifário' : 'Novo Tarifário'}</DialogTitle>
          <DialogDescription>Preços, ciclos, potências e descontos do tarifário BTN.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Comercializadora</Label>
              <Select value={form.commercializer_id} onValueChange={(v) => setForm({ ...form, commercializer_id: v })}>
                <SelectTrigger data-testid="tariff-commercializer-select"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                <SelectContent>
                  {commercializers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nome do Tarifário</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tariff-name-input" placeholder="Ex: Continente Light Verde" />
            </div>
            <div className="col-span-2">
              <Label className="mb-2 block">Tipo de Energia</Label>
              <div className="grid grid-cols-3 gap-3" data-testid="tariff-fuel-buttons">
                {[
                  { v: 'luz', icon: '⚡', label: 'Luz', desc: 'Só electricidade', color: 'amber' },
                  { v: 'gas', icon: '🔥', label: 'Gás', desc: 'Só gás natural', color: 'orange' },
                  { v: 'dual', icon: '✨', label: 'Dual', desc: 'Luz + Gás na mesma factura', color: 'indigo' },
                ].map((opt) => {
                  const active = (form.fuel || 'luz') === opt.v;
                  const colorMap = {
                    amber: active ? 'ring-2 ring-amber-400 bg-amber-50 border-amber-400' : 'border-slate-200 hover:bg-amber-50/50',
                    orange: active ? 'ring-2 ring-orange-400 bg-orange-50 border-orange-400' : 'border-slate-200 hover:bg-orange-50/50',
                    indigo: active ? 'ring-2 ring-indigo-400 bg-indigo-50 border-indigo-400' : 'border-slate-200 hover:bg-indigo-50/50',
                  };
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setForm({ ...form, fuel: opt.v })}
                      data-testid={`tariff-fuel-${opt.v}-btn`}
                      className={`flex flex-col items-center gap-1 p-3 border-2 rounded-lg transition-all ${colorMap[opt.color]}`}
                    >
                      <span className="text-2xl">{opt.icon}</span>
                      <span className="font-semibold text-sm text-slate-800">{opt.label}</span>
                      <span className="text-[10px] text-slate-500 text-center">{opt.desc}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label>Segmento</Label>
              <Select value={form.segment} onValueChange={(v) => setForm({ ...form, segment: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{SEGMENTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Permanência (meses)</Label>
              <Input type="number" value={form.permanencia_meses} onChange={(e) => setForm({ ...form, permanencia_meses: e.target.value })} />
            </div>
          </div>

          {/* ======= LUZ (mostra em modo Luz ou Dual) ======= */}
          {((form.fuel || 'luz') === 'luz' || form.fuel === 'dual') && (
            <>
              {form.fuel === 'dual' && (
                <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 -mb-2">
                  <span className="text-sm font-semibold text-amber-800">⚡ Secção LUZ</span>
                </div>
              )}
              <div>
                <Label className="mb-2 block">Ciclo (cada tarifário tem um único ciclo)</Label>
                <div className="flex gap-3 flex-wrap" data-testid="tariff-ciclo-radio">
                  {CICLOS.map((c) => {
                    const isActive = getCicloUnico(form) === c.value;
                    return (
                      <label
                        key={c.value}
                        className={`flex items-center gap-2 px-3 py-2 border rounded cursor-pointer transition ${
                          isActive ? 'bg-[#2D3E6F] text-white border-[#2D3E6F]' : 'bg-white hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="tariff-ciclo"
                          value={c.value}
                          checked={isActive}
                          onChange={() => setCiclo(c.value)}
                          className="accent-[#2D3E6F]"
                          data-testid={`tariff-ciclo-${c.value}`}
                        />
                        <span className="text-sm font-medium">{c.label}</span>
                        <span className={`text-[10px] ${isActive ? 'text-amber-200' : 'text-slate-400'}`}>({c.tipo})</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  {getCicloUnico(form) === 'tri_horario'
                    ? '⚡ BT Especial — potências 27.60, 34.50, 41.40 kVA'
                    : getCicloUnico(form) === 'bi_horario'
                    ? '🏠 BT Normal — potências 3.45 → 20.70 kVA'
                    : '🏠 BT Normal — potências 1.15 → 20.70 kVA'}
                </p>
              </div>

              <div>
                <Label className="mb-2 block">Termo Potência (€/dia) por kVA</Label>
                <p className="text-[11px] text-slate-500 mb-2">Preço final (já com descontos permanentes incluídos). Deixa a 0 as potências que não vais oferecer.</p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 bg-slate-50 p-2 rounded">
                  {(form.potencias_pricing || []).map((p, idx) => (
                    <div key={p.potencia} className="bg-white border rounded p-1.5">
                      <div className="text-[11px] text-slate-500 font-medium">{Number(p.potencia).toFixed(2)} kVA</div>
                      <Input
                        type="number"
                        step="0.0001"
                        className="h-7 text-sm"
                        value={p.termo_fixo_eur_dia ?? ''}
                        onChange={(e) => updatePotenciaPrice(idx, e.target.value)}
                        placeholder="0.0000"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Campos de preço €/kWh — apenas os relevantes para o ciclo selecionado */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {getCicloUnico(form) === 'simples' && (
                  <div className="md:col-span-3">
                    <Label>Preço Simples (€/kWh)</Label>
                    <Input type="number" step="0.0001" value={form.preco_simples_kwh} onChange={(e) => setForm({ ...form, preco_simples_kwh: e.target.value })} data-testid="tariff-preco-simples" />
                  </div>
                )}
                {getCicloUnico(form) === 'bi_horario' && (
                  <>
                    <div>
                      <Label>Preço Cheia (€/kWh)</Label>
                      <Input type="number" step="0.0001" value={form.preco_cheia_kwh} onChange={(e) => setForm({ ...form, preco_cheia_kwh: e.target.value })} data-testid="tariff-preco-cheia" />
                    </div>
                    <div>
                      <Label>Preço Vazio (€/kWh)</Label>
                      <Input type="number" step="0.0001" value={form.preco_vazio_kwh} onChange={(e) => setForm({ ...form, preco_vazio_kwh: e.target.value })} data-testid="tariff-preco-vazio" />
                    </div>
                  </>
                )}
                {getCicloUnico(form) === 'tri_horario' && (
                  <>
                    <div>
                      <Label>Preço Ponta (€/kWh)</Label>
                      <Input type="number" step="0.0001" value={form.preco_ponta_kwh} onChange={(e) => setForm({ ...form, preco_ponta_kwh: e.target.value })} data-testid="tariff-preco-ponta" />
                    </div>
                    <div>
                      <Label>Preço Cheia (€/kWh)</Label>
                      <Input type="number" step="0.0001" value={form.preco_cheia_kwh} onChange={(e) => setForm({ ...form, preco_cheia_kwh: e.target.value })} data-testid="tariff-preco-cheia-tri" />
                    </div>
                    <div>
                      <Label>Preço Vazio (€/kWh)</Label>
                      <Input type="number" step="0.0001" value={form.preco_vazio_kwh} onChange={(e) => setForm({ ...form, preco_vazio_kwh: e.target.value })} data-testid="tariff-preco-vazio-tri" />
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {/* ======= GÁS (mostra em modo Gás ou Dual) ======= */}
          {(form.fuel === 'gas' || form.fuel === 'dual') && (
            <div>
              {form.fuel === 'dual' && (
                <div className="bg-orange-50 border border-orange-200 rounded-md px-3 py-2 mb-3">
                  <span className="text-sm font-semibold text-orange-800">🔥 Secção GÁS</span>
                </div>
              )}
              <Label className="mb-2 block">Escalões de Gás</Label>
              <p className="text-[11px] text-slate-500 mb-2">Termo fixo €/dia + Preço €/kWh para cada escalão (1 a 4). Preço final, já com descontos permanentes.</p>
              <div className="bg-slate-50 p-2 rounded space-y-2">
                <div className="grid grid-cols-12 gap-2 text-[11px] text-slate-500 px-1">
                  <div className="col-span-2">Escalão</div>
                  <div className="col-span-5">Termo Fixo (€/dia)</div>
                  <div className="col-span-5">Preço Energia (€/kWh)</div>
                </div>
                {(form.escaloes || []).map((e, idx) => (
                  <div key={e.escalao} className="grid grid-cols-12 gap-2 items-center bg-white border rounded p-1.5">
                    <div className="col-span-2 font-semibold text-slate-700 pl-2">Escalão {e.escalao}</div>
                    <Input
                      className="col-span-5 h-8 text-sm"
                      type="number" step="0.0001"
                      value={e.termo_fixo_eur_dia ?? ''}
                      onChange={(ev) => updateEscalao(idx, { termo_fixo_eur_dia: ev.target.value })}
                      placeholder="0.0000"
                    />
                    <Input
                      className="col-span-5 h-8 text-sm"
                      type="number" step="0.000001"
                      value={e.preco_kwh ?? ''}
                      onChange={(ev) => updateEscalao(idx, { preco_kwh: ev.target.value })}
                      placeholder="0.000000"
                    />
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-slate-400 mt-1.5">Escalão 1: ≤ 220 kWh/ano · Escalão 2: 221-500 · Escalão 3: 501-1000 · Escalão 4: &gt; 1000 kWh/ano</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Válido desde</Label>
              <Input type="date" value={form.valid_from || ''} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} />
            </div>
            <div>
              <Label>Válido até</Label>
              <Input type="date" value={form.valid_to || ''} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })} id="tariff-active" />
            <Label htmlFor="tariff-active">Activo</Label>
          </div>

          <div>
            <Label>Notas internas</Label>
            <Textarea rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-base">Descontos</Label>
              <Button size="sm" variant="outline" onClick={addDiscount} data-testid="add-discount-btn"><Plus className="w-4 h-4 mr-1" /> Adicionar</Button>
            </div>
            {(form.discounts || []).length === 0 && (
              <div className="text-sm text-slate-500 italic">Sem descontos. Adicione DD, FE, Dual ou outros.</div>
            )}
            <div className="space-y-2">
              {(form.discounts || []).map((d, idx) => (
                <div key={idx} className="bg-slate-50 p-2 rounded space-y-2">
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-3" placeholder="Nome (ex: DD)" value={d.name} onChange={(e) => updateDiscount(idx, { name: e.target.value })} />
                    <Select value={d.type} onValueChange={(v) => updateDiscount(idx, { type: v })}>
                      <SelectTrigger className="col-span-4"><SelectValue /></SelectTrigger>
                      <SelectContent>{DISCOUNT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                    {d.type !== 'bonus_temporario' && (
                      <Input className="col-span-2" type="number" step="0.01" placeholder="Valor" value={d.value} onChange={(e) => updateDiscount(idx, { value: e.target.value })} />
                    )}
                    {d.type === 'bonus_temporario' && <div className="col-span-2" />}
                    <label className="col-span-2 flex items-center gap-1 text-xs">
                      <Checkbox checked={d.active !== false} onCheckedChange={(v) => updateDiscount(idx, { active: !!v })} />
                      Activo
                    </label>
                    <Button size="icon" variant="ghost" onClick={() => removeDiscount(idx)} className="col-span-1"><X className="w-4 h-4 text-rose-500" /></Button>
                  </div>
                  {d.type === 'bonus_temporario' && (
                    <div className="grid grid-cols-4 gap-2 pl-2 border-l-2 border-amber-300">
                      <div className="bg-amber-50/50 rounded p-1.5 border border-amber-200">
                        <Label className="text-[10px] text-amber-800 font-semibold flex items-center gap-1">⚡ Só Luz (€/mês)</Label>
                        <Input type="number" step="0.01" placeholder="ex: 10" value={d.monthly_eur || ''} onChange={(e) => updateDiscount(idx, { monthly_eur: e.target.value })} />
                      </div>
                      <div className="bg-orange-50/50 rounded p-1.5 border border-orange-200">
                        <Label className="text-[10px] text-orange-800 font-semibold flex items-center gap-1">🔥 Só Gás (€/mês)</Label>
                        <Input type="number" step="0.01" placeholder="ex: 7.50" value={d.monthly_eur_gas || ''} onChange={(e) => updateDiscount(idx, { monthly_eur_gas: e.target.value })} />
                      </div>
                      <div className="bg-indigo-50/50 rounded p-1.5 border border-indigo-200">
                        <Label className="text-[10px] text-indigo-800 font-semibold flex items-center gap-1">✨ Dual L+G (€/mês)</Label>
                        <Input type="number" step="0.01" placeholder="ex: 17.50" value={d.monthly_eur_dual || ''} onChange={(e) => updateDiscount(idx, { monthly_eur_dual: e.target.value })} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-slate-500">Duração (meses)</Label>
                        <Input type="number" placeholder="ex: 4" value={d.meses || ''} onChange={(e) => updateDiscount(idx, { meses: e.target.value })} />
                      </div>
                      <p className="col-span-4 text-[11px] text-amber-700 bg-amber-50 px-2 py-1 rounded">
                        💡 Exemplo Endesa: <b>10€/mês (Luz só)</b> · <b>7,50€/mês (Gás só)</b> · <b>17,50€/mês (Dual)</b> · 4 meses. Distribuição total: 40€ luz + 30€ gás = 70€.
                      </p>
                    </div>
                  )}
                  {d.type === 'flexivel' && (
                    <div className="grid grid-cols-12 gap-2 pl-2 border-l-2 border-indigo-300 text-xs">
                      <div className="col-span-2">
                        <Label className="text-[10px] text-slate-500">Tipo valor</Label>
                        <Select value={d.value_type || 'percent'} onValueChange={(v) => updateDiscount(idx, { value_type: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percent">% Percentagem</SelectItem>
                            <SelectItem value="eur">€ Valor fixo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-3">
                        <Label className="text-[10px] text-slate-500">Aplicar a</Label>
                        <div className="flex gap-1.5 items-center mt-1" data-testid={`discount-scope-${idx}`}>
                          {[
                            { m: 'luz', icon: '⚡', label: 'Luz', activeCls: 'bg-amber-100 border-amber-400 text-amber-800' },
                            { m: 'gas', icon: '🔥', label: 'Gás', activeCls: 'bg-orange-100 border-orange-400 text-orange-800' },
                            { m: 'dual', icon: '✨', label: 'Dual', activeCls: 'bg-indigo-100 border-indigo-400 text-indigo-800' },
                          ].map(({ m, icon, label, activeCls }) => {
                            const isActive = (d.aplicar_a || []).includes(m);
                            return (
                              <button
                                key={m}
                                type="button"
                                onClick={() => {
                                  const arr = new Set(d.aplicar_a || []);
                                  if (isActive) arr.delete(m); else arr.add(m);
                                  updateDiscount(idx, { aplicar_a: Array.from(arr) });
                                }}
                                data-testid={`discount-scope-${idx}-${m}`}
                                className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded border-2 text-[10px] font-medium transition ${
                                  isActive ? activeCls : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                                }`}
                              >
                                <span className="text-base leading-none">{icon}</span>
                                <span>{label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="col-span-3">
                        <Label className="text-[10px] text-slate-500">Base</Label>
                        <Select value={d.base || 'termo_energia'} onValueChange={(v) => updateDiscount(idx, { base: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{FLEX_BASES.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[10px] text-slate-500">Duração (m)</Label>
                        <Input type="number" placeholder="0=sempre" value={d.duracao_meses || ''} onChange={(e) => updateDiscount(idx, { duracao_meses: e.target.value })} />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[10px] text-slate-500">Condições</Label>
                        <div className="flex gap-1 items-center mt-1 flex-wrap">
                          {[['dd', 'DD'], ['fe', 'FE'], ['ok', 'OK']].map(([k, lbl]) => (
                            <label key={k} className="flex items-center gap-0.5">
                              <Checkbox
                                checked={(d.condicoes_required || {})[k] === true}
                                onCheckedChange={(v) => {
                                  const cond = { ...(d.condicoes_required || {}) };
                                  cond[k] = !!v;
                                  updateDiscount(idx, { condicoes_required: cond });
                                }}
                              />
                              <span className="text-[10px]">{lbl}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <p className="col-span-12 text-[11px] text-indigo-700 bg-indigo-50 px-2 py-1 rounded">
                        💡 Ex.: 8% sobre Termo+Energia, aplicar a "Luz" e "Gás", 12 meses, sem condições obrigatórias. Para Endesa Dual 2%: aplicar a "Dual", 0m (sempre), sem condições.
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="tariff-save-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const TarifariosAdminPage = () => {
  const { user } = useAuth();
  const [coms, setComs] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCom, setActiveCom] = useState(null);
  const [segmentTab, setSegmentTab] = useState('residencial');
  const [fuelTab, setFuelTab] = useState('luz');

  const [comDialog, setComDialog] = useState({ open: false, initial: null });
  const [tariffDialog, setTariffDialog] = useState({ open: false, initial: null });

  const isAdmin = user?.role === 'super_admin' || user?.role === 'gestor';

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API_URL}/simulador/commercializers`),
        axios.get(`${API_URL}/simulador/tariffs`),
      ]);
      setComs(r1.data || []);
      setTariffs(r2.data || []);
      if (!activeCom && r1.data?.length) setActiveCom(r1.data[0].id);
    } catch (e) {
      toast.error('Erro a carregar tarifários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filteredTariffs = useMemo(() => {
    return tariffs.filter((t) => {
      if (t.commercializer_id !== activeCom) return false;
      if (t.segment !== segmentTab) return false;
      const tFuel = t.fuel || 'luz';
      return tFuel === fuelTab;
    });
  }, [tariffs, activeCom, segmentTab, fuelTab]);

  const deleteCom = async (c) => {
    if (!window.confirm(`Eliminar "${c.name}"? Todos os tarifários desta comercializadora serão removidos.`)) return;
    try {
      await axios.delete(`${API_URL}/simulador/commercializers/${c.id}`);
      toast.success('Eliminada');
      await load();
      if (activeCom === c.id) setActiveCom(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro');
    }
  };

  const deleteTariff = async (t) => {
    if (!window.confirm(`Eliminar tarifário "${t.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/simulador/tariffs/${t.id}`);
      toast.success('Eliminado');
      load();
    } catch (e) {
      const st = e?.response?.status;
      toast.error(`Erro ao eliminar${st ? ` (${st})` : ''}: ${e?.response?.data?.detail || e?.message || 'sem resposta do servidor'}`);
    }
  };

  const duplicateTariff = (t) => {
    // Cria um clone editável do tarifário e abre o diálogo (sem gravar ainda)
    // Útil para criar variações rápidas: ex. clonar "Luz" e converter em "Dual"
    const { id, created_at, ...rest } = t;  // eslint-disable-line no-unused-vars
    const clone = {
      ...rest,
      name: `${t.name} (cópia)`,
    };
    setTariffDialog({ open: true, initial: clone });
    toast.info('Tarifário duplicado. Ajusta os campos e grava.');
  };

  const toggleTariffActive = async (t) => {
    try {
      await axios.put(`${API_URL}/simulador/tariffs/${t.id}`, { active: !t.active });
      load();
    } catch (e) {
      toast.error('Erro');
    }
  };

  if (!isAdmin) {
    return (
      <Layout>
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-rose-400 mx-auto mb-3" />
          <p className="text-lg font-semibold text-slate-800">Acesso restrito</p>
          <p className="text-sm text-slate-500 mt-1">Apenas Super Admin ou Gestor.</p>
        </div>
      </Layout>
    );
  }

  const activeComObj = coms.find((c) => c.id === activeCom);

  return (
    <Layout>
      <div className="space-y-5" data-testid="tarifarios-admin-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Zap className="w-6 h-6 text-amber-500" /> Tarifários de Energia
            </h1>
            <p className="text-sm text-slate-500 mt-1">Catálogo manual de tarifários BTN — alimenta o Simulador de Poupança.</p>
          </div>
          <Button onClick={() => setComDialog({ open: true, initial: null })} data-testid="new-commercializer-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white">
            <Plus className="w-4 h-4 mr-1" /> Nova Comercializadora
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" /></div>
        ) : (
          <div className="grid grid-cols-12 gap-5">
            {/* Sidebar of commercializers */}
            <Card className="col-span-12 md:col-span-3">
              <CardHeader className="pb-3"><CardTitle className="text-sm">Comercializadoras</CardTitle></CardHeader>
              <CardContent className="space-y-1 px-2 pb-3">
                {coms.length === 0 && <div className="text-xs text-slate-400 italic px-2 py-3">Vazio</div>}
                {coms.map((c) => {
                  const tariffCount = tariffs.filter((t) => t.commercializer_id === c.id).length;
                  return (
                  <div
                    key={c.id}
                    className={`group flex items-center justify-between px-3 py-2 rounded cursor-pointer hover:bg-slate-100 ${activeCom === c.id ? 'bg-indigo-50 border border-indigo-200' : ''}`}
                    onClick={() => setActiveCom(c.id)}
                    data-testid={`commercializer-row-${c.name}`}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: c.color }} />
                      <span className="text-sm font-medium truncate">{c.name}</span>
                      {!c.active && <Badge variant="outline" className="text-[10px] py-0">off</Badge>}
                      {tariffCount > 0 && (
                        <Badge variant="secondary" className="text-[10px] py-0 px-1.5 ml-auto bg-slate-100 text-slate-600 group-hover:bg-white">
                          {tariffCount}
                        </Badge>
                      )}
                      {tariffCount === 0 && (
                        <Badge variant="outline" className="text-[10px] py-0 px-1.5 ml-auto text-slate-300 border-slate-200">
                          0
                        </Badge>
                      )}
                    </div>
                    <div className="opacity-0 group-hover:opacity-100 flex gap-1 ml-1">
                      <button onClick={(e) => { e.stopPropagation(); setComDialog({ open: true, initial: c }); }} className="p-1 hover:bg-slate-200 rounded"><Pencil className="w-3 h-3" /></button>
                      <button onClick={(e) => { e.stopPropagation(); deleteCom(c); }} className="p-1 hover:bg-rose-100 rounded"><Trash2 className="w-3 h-3 text-rose-500" /></button>
                    </div>
                  </div>
                  );
                })}
                <div className="px-3 pt-2 mt-2 border-t border-slate-100">
                  <p className="text-[10px] text-slate-400">
                    Total: <span className="font-semibold text-slate-600">{tariffs.length}</span> tarifários
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Tariffs grid */}
            <Card className="col-span-12 md:col-span-9">
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  {activeComObj && <span className="w-4 h-4 rounded-full" style={{ background: activeComObj.color }} />}
                  <CardTitle className="text-base">{activeComObj?.name || 'Selecione uma comercializadora'}</CardTitle>
                </div>
                {activeCom && (
                  <Button size="sm" onClick={() => setTariffDialog({ open: true, initial: null, fuel: fuelTab })} data-testid="new-tariff-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                    <Plus className="w-4 h-4 mr-1" /> Novo Tarifário
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {!activeCom ? (
                  <div className="text-center py-12 text-slate-400 text-sm">Escolha uma comercializadora à esquerda.</div>
                ) : (
                  <div className="space-y-3">
                    <Tabs value={fuelTab} onValueChange={setFuelTab}>
                      <TabsList>
                        <TabsTrigger value="luz" data-testid="fuel-tab-luz">
                          ⚡ Luz {activeCom && (
                            <span className="ml-1.5 text-[10px] bg-amber-100 text-amber-700 rounded px-1.5">
                              {tariffs.filter((t) => t.commercializer_id === activeCom && t.segment === segmentTab && (t.fuel || 'luz') === 'luz').length}
                            </span>
                          )}
                        </TabsTrigger>
                        <TabsTrigger value="gas" data-testid="fuel-tab-gas">
                          🔥 Gás {activeCom && (
                            <span className="ml-1.5 text-[10px] bg-orange-100 text-orange-700 rounded px-1.5">
                              {tariffs.filter((t) => t.commercializer_id === activeCom && t.segment === segmentTab && t.fuel === 'gas').length}
                            </span>
                          )}
                        </TabsTrigger>
                        <TabsTrigger value="dual" data-testid="fuel-tab-dual">
                          ✨ Dual {activeCom && (
                            <span className="ml-1.5 text-[10px] bg-indigo-100 text-indigo-700 rounded px-1.5">
                              {tariffs.filter((t) => t.commercializer_id === activeCom && t.segment === segmentTab && t.fuel === 'dual').length}
                            </span>
                          )}
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                    <Tabs value={segmentTab} onValueChange={setSegmentTab}>
                      <TabsList>
                        <TabsTrigger value="residencial" data-testid="seg-tab-residencial">Residencial</TabsTrigger>
                        <TabsTrigger value="empresarial" data-testid="seg-tab-empresarial">Empresarial</TabsTrigger>
                      </TabsList>
                      <TabsContent value={segmentTab} className="mt-4">
                      {filteredTariffs.length === 0 ? (
                        <div className="text-center py-12 text-slate-400 text-sm">Sem tarifários neste segmento.</div>
                      ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                          {filteredTariffs.map((t) => (
                            <div key={t.id} className={`border rounded-lg p-3 hover:shadow-sm transition ${t.active ? '' : 'opacity-60'}`} data-testid={`tariff-card-${t.id}`}>
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="font-semibold text-slate-800 truncate">{t.name}</div>
                                  <div className="text-xs text-slate-500 mt-0.5 flex gap-2 flex-wrap">
                                    {(t.fuel || 'luz') === 'gas' ? (
                                      <Badge variant="secondary" className="text-[10px] bg-orange-100 text-orange-700 border-orange-300 border">🔥 Gás · {(t.escaloes || []).filter((e) => Number(e.termo_fixo_eur_dia) > 0 || Number(e.preco_kwh) > 0).length}/4 escalões</Badge>
                                    ) : (t.fuel === 'dual') ? (
                                      <>
                                        <Badge variant="secondary" className="text-[10px] bg-indigo-100 text-indigo-700 border-indigo-300 border">✨ Dual (Luz+Gás)</Badge>
                                        {(t.ciclos || []).map((c) => <Badge key={c} variant="secondary" className="text-[10px]">{c}</Badge>)}
                                        <Badge variant="outline" className="text-[10px] bg-orange-50 border-orange-200 text-orange-700">{(t.escaloes || []).filter((e) => Number(e.termo_fixo_eur_dia) > 0 || Number(e.preco_kwh) > 0).length}/4 escalões</Badge>
                                      </>
                                    ) : (
                                      <>
                                        <Badge variant="secondary" className="text-[10px] bg-amber-100 text-amber-700 border-amber-300 border">⚡ Luz</Badge>
                                        {(t.ciclos || []).map((c) => <Badge key={c} variant="secondary" className="text-[10px]">{c}</Badge>)}
                                        {(t.potencias_pricing || []).filter((p) => Number(p.termo_fixo_eur_dia) > 0).length > 0 && (
                                          <Badge variant="outline" className="text-[10px]">{(t.potencias_pricing || []).filter((p) => Number(p.termo_fixo_eur_dia) > 0).length} potências</Badge>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button onClick={() => toggleTariffActive(t)} className="p-1.5 hover:bg-slate-100 rounded" title={t.active ? 'Desactivar' : 'Activar'}>
                                    {t.active ? <Power className="w-4 h-4 text-emerald-500" /> : <PowerOff className="w-4 h-4 text-slate-400" />}
                                  </button>
                                  <button onClick={() => setTariffDialog({ open: true, initial: t })} className="p-1.5 hover:bg-slate-100 rounded" title="Editar"><Pencil className="w-4 h-4" /></button>
                                  <button onClick={() => duplicateTariff(t)} className="p-1.5 hover:bg-indigo-100 rounded" title="Duplicar (cria cópia editável)" data-testid={`duplicate-tariff-${t.id}`}><Copy className="w-4 h-4 text-indigo-500" /></button>
                                  <button onClick={() => deleteTariff(t)} className="p-1.5 hover:bg-rose-100 rounded" title="Eliminar"><Trash2 className="w-4 h-4 text-rose-500" /></button>
                                </div>
                              </div>
                              {(t.fuel || 'luz') === 'gas' ? (
                                <div className="mt-2 grid grid-cols-2 gap-1 text-[11px]">
                                  {(t.escaloes || []).map((e) => (
                                    <div key={e.escalao} className="bg-slate-50 px-2 py-1 rounded">
                                      <span className="font-medium text-slate-600">E{e.escalao}:</span> {Number(e.termo_fixo_eur_dia || 0).toFixed(4)} €/d · {Number(e.preco_kwh || 0).toFixed(4)} €/kWh
                                    </div>
                                  ))}
                                </div>
                              ) : t.fuel === 'dual' ? (
                                <div className="mt-2 space-y-1.5 text-xs">
                                  <div className="bg-amber-50 px-2 py-1 rounded border border-amber-200">
                                    <span className="text-[10px] font-semibold text-amber-700">⚡ LUZ</span>{' '}
                                    {t.ciclos?.includes('simples') && <span className="font-semibold">{Number(t.preco_simples_kwh || 0).toFixed(4)} €/kWh</span>}
                                    {t.ciclos?.includes('bi_horario') && <span className="font-semibold">C/V {Number(t.preco_cheia_kwh || 0).toFixed(3)} / {Number(t.preco_vazio_kwh || 0).toFixed(3)}</span>}
                                    {t.ciclos?.includes('tri_horario') && <span className="font-semibold">P/C/V {Number(t.preco_ponta_kwh || 0).toFixed(3)} / {Number(t.preco_cheia_kwh || 0).toFixed(3)} / {Number(t.preco_vazio_kwh || 0).toFixed(3)}</span>}
                                  </div>
                                  <div className="bg-orange-50 px-2 py-1 rounded border border-orange-200">
                                    <span className="text-[10px] font-semibold text-orange-700">🔥 GÁS</span>{' '}
                                    {(t.escaloes || []).filter((e) => Number(e.preco_kwh) > 0).map((e) => (
                                      <span key={e.escalao} className="mr-2">E{e.escalao}: <b>{Number(e.preco_kwh || 0).toFixed(4)}</b></span>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <div className="mt-2 grid grid-cols-2 gap-1.5 text-xs">
                                  {t.ciclos?.includes('simples') && <div className="bg-slate-50 px-2 py-1 rounded col-span-2">Simples <span className="font-semibold">{Number(t.preco_simples_kwh || 0).toFixed(4)} €/kWh</span></div>}
                                  {t.ciclos?.includes('bi_horario') && <div className="bg-slate-50 px-2 py-1 rounded col-span-2">C/V <span className="font-semibold">{Number(t.preco_cheia_kwh || 0).toFixed(3)} / {Number(t.preco_vazio_kwh || 0).toFixed(3)}</span></div>}
                                  {t.ciclos?.includes('tri_horario') && <div className="bg-slate-50 px-2 py-1 rounded col-span-2">P/C/V <span className="font-semibold">{Number(t.preco_ponta_kwh || 0).toFixed(3)} / {Number(t.preco_cheia_kwh || 0).toFixed(3)} / {Number(t.preco_vazio_kwh || 0).toFixed(3)}</span></div>}
                                </div>
                              )}
                              {(t.discounts || []).length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {(t.discounts || []).map((d, i) => {
                                    if (d.type === 'bonus_temporario') {
                                      const vLuz = d.monthly_eur || 0;
                                      const vGas = d.monthly_eur_gas || 0;
                                      const vDual = d.monthly_eur_dual || 0;
                                      const mm = d.meses || 0;
                                      const tFuel = (t.fuel === 'gas') ? 'gas' : (t.fuel === 'dual') ? 'dual' : 'luz';
                                      let parts;
                                      if (tFuel === 'dual') {
                                        const arr = [];
                                        if (vLuz > 0) arr.push(`L -${vLuz}€/m`);
                                        if (vGas > 0) arr.push(`G -${vGas}€/m`);
                                        if (vDual > 0) arr.push(`Dual -${vDual}€/m`);
                                        parts = arr.join(' · ');
                                      } else {
                                        const ownLabel = tFuel === 'gas' && vGas > 0
                                          ? `Gás -${vGas}€/m`
                                          : (tFuel === 'luz' && vLuz > 0 ? `Luz -${vLuz}€/m` : null);
                                        const dualLabel = vDual > 0 ? `Dual -${vDual}€` : null;
                                        parts = [ownLabel, dualLabel].filter(Boolean).join(' · ');
                                      }
                                      return (
                                        <Badge key={i} className="text-[10px] gap-1 bg-amber-100 text-amber-800 border-amber-300 border">
                                          <Tag className="w-2.5 h-2.5" /> {d.name || 'Bónus'} · {parts || `-${vLuz}€/m`} × {mm}m
                                        </Badge>
                                      );
                                    }
                                    const isPct = d.type.includes('percent') || d.type === 'debito_directo' || d.type === 'factura_electronica' || d.type === 'dual' || d.type === 'permanencia';
                                    // Escopos visuais para descontos flexíveis
                                    const scopes = (d.type === 'flexivel' && Array.isArray(d.aplicar_a)) ? d.aplicar_a : [];
                                    const scopeIcons = scopes.map((s) => s === 'luz' ? '⚡' : s === 'gas' ? '🔥' : '✨').join('');
                                    return (
                                      <Badge key={i} variant="outline" className="text-[10px] gap-1">
                                        <Tag className="w-2.5 h-2.5" />
                                        {scopeIcons && <span className="text-[11px] leading-none">{scopeIcons}</span>}
                                        {d.name || d.type} · {d.value}{isPct ? '%' : '€'}
                                      </Badge>
                                    );
                                  })}
                                </div>
                              )}
                              {t.permanencia_meses > 0 && <div className="text-[11px] text-slate-500 mt-1.5">Permanência: {t.permanencia_meses} meses</div>}
                            </div>
                          ))}
                        </div>
                      )}
                    </TabsContent>
                    </Tabs>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <CommercializerDialog
        open={comDialog.open}
        onOpenChange={(o) => setComDialog({ ...comDialog, open: o })}
        initial={comDialog.initial}
        onSaved={load}
      />

      <TariffDialog
        open={tariffDialog.open}
        onOpenChange={(o) => setTariffDialog({ ...tariffDialog, open: o })}
        initial={tariffDialog.initial}
        defaultCommercializerId={activeCom}
        defaultFuel={tariffDialog.fuel || fuelTab}
        commercializers={coms}
        onSaved={load}
      />
    </Layout>
  );
};

export default TarifariosAdminPage;
